import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearch } from '../utils/searchStorage';
import { 
  Search, 
  Zap, 
  ArrowRight, 
  Lock, 
  Star, 
  TrendingUp, 
  Eye, 
  Brain, 
  Target, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  X,
  Wifi, 
  WifiOff,
  RefreshCw,
  Filter,
  SortDesc,
  BarChart3,
  Clock,
  Globe,
  Users,
  Activity,
  Sparkles,
  Heart,
  MessageCircle,
  Bookmark,
  ExternalLink,
  Award,
  Flame,
  Shield,
  ChevronDown,
  ChevronUp,
  Share2,
  Flag,
  Building2,
  Copy,
  Check,
  Twitter,
  Facebook,
  Linkedin,
  Mail,
  Send,
  Link2
} from "lucide-react";

// Enhanced Share Modal Component
const EnhancedShareModal = ({ analysis, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    if (analysis) {
      const url = `${window.location.origin}/analysis/${analysis.id}`;
      setShareUrl(url);
    }
  }, [analysis]);

  if (!isOpen || !analysis) return null;

  const shareData = {
    title: `Website Analysis: ${analysis.url}`,
    text: `Check out this AI analysis of ${analysis.url} - Overall Score: ${analysis.score || analysis.overallScore}/100! 🚀`,
    url: shareUrl,
    hashtags: 'WebAnalysis,AI,WebDev,Performance'
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.text)}&url=${encodeURIComponent(shareData.url)}&hashtags=${shareData.hashtags}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}&quote=${encodeURIComponent(shareData.text)}`;
    window.open(url, '_blank', 'width=580,height=400');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareData.url)}&title=${encodeURIComponent(shareData.title)}&summary=${encodeURIComponent(shareData.text)}`;
    window.open(url, '_blank', 'width=520,height=570');
  };

  const shareToReddit = () => {
    const url = `https://reddit.com/submit?url=${encodeURIComponent(shareData.url)}&title=${encodeURIComponent(shareData.title)}`;
    window.open(url, '_blank', 'width=600,height=500');
  };

  const shareToWhatsApp = () => {
    const text = `${shareData.text}\n\n${shareData.url}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareToTelegram = () => {
    const text = `${shareData.text}\n\n${shareData.url}`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.text)}`;
    window.open(url, '_blank');
  };

  const shareToEmail = () => {
    const subject = encodeURIComponent(shareData.title);
    const body = encodeURIComponent(`${shareData.text}\n\nView the full analysis here: ${shareData.url}\n\nPowered by CodeRated - AI Website Analysis`);
    const url = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = url;
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareData.title,
          text: shareData.text,
          url: shareData.url
        });
      } catch (error) {
        console.error('Native share failed:', error);
      }
    }
  };

  const sharePlatforms = [
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'hover:bg-blue-500/20 hover:text-blue-400',
      action: shareToTwitter
    },
    {
      name: 'Facebook', 
      icon: Facebook,
      color: 'hover:bg-blue-600/20 hover:text-blue-500',
      action: shareToFacebook
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'hover:bg-blue-700/20 hover:text-blue-600',
      action: shareToLinkedIn
    },
    {
      name: 'Reddit',
      icon: MessageCircle,
      color: 'hover:bg-orange-500/20 hover:text-orange-400',
      action: shareToReddit
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'hover:bg-green-500/20 hover:text-green-400',
      action: shareToWhatsApp
    },
    {
      name: 'Telegram',
      icon: Send,
      color: 'hover:bg-blue-400/20 hover:text-blue-300',
      action: shareToTelegram
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'hover:bg-gray-500/20 hover:text-gray-300',
      action: shareToEmail
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in duration-200">
        
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-bold text-white">Share Analysis</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          
          <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {analysis.score || analysis.overallScore}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold truncate">{analysis.url}</h4>
                <p className="text-slate-400 text-sm">
                  AI Analysis Score: {analysis.score || analysis.overallScore}/100
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  {analysis.demo_mode ? '🎭 Demo Analysis' : '🤖 AI-Powered Analysis'}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Share Link
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 min-w-0">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="w-full bg-transparent text-slate-300 text-sm focus:outline-none truncate"
                />
              </div>
              <button
                onClick={handleCopyLink}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex-shrink-0 ${
                  copied 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                }`}
              >
                {copied ? (
                  <div className="flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </div>
                )}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-300">
              Share to Platform
            </label>
            
            <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
              {sharePlatforms.map((platform, index) => {
                const IconComponent = platform.icon;
                return (
                  <button
                    key={index}
                    onClick={() => {
                      platform.action();
                    }}
                    className={`flex items-center gap-3 w-full p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-all ${platform.color} group`}
                  >
                    <IconComponent className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium flex-1 text-left">Share on {platform.name}</span>
                    <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
                  </button>
                );
              })}
            </div>

            {navigator.share && (
              <button
                onClick={handleNativeShare}
                className="flex items-center gap-3 w-full p-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-lg transition-all hover:from-blue-500/30 hover:to-purple-500/30 group"
              >
                <Share2 className="w-5 h-5" />
                <span className="font-medium flex-1 text-left">More Sharing Options</span>
                <span className="text-xs text-blue-400 opacity-80 group-hover:opacity-100">Native</span>
              </button>
            )}
          </div>

          <div className="mt-6 p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
            <p className="text-slate-400 text-xs text-center flex items-center justify-center gap-1">
              <Link2 className="w-3 h-3" />
              Shared links help grow our community and improve AI analysis for everyone
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact Report Modal
const CompactReportModal = ({ 
  analysis, 
  isOpen, 
  onClose, 
  onLike, 
  currentUser,
  currentTier = 'free' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(analysis?.likes || 0);
  const [viewCount, setViewCount] = useState(analysis?.views || 0);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (analysis?.likedBy && currentUser?.id) {
      setIsLiked(analysis.likedBy.includes(currentUser.id));
    }
    setLikeCount(analysis?.likes || 0);
    setViewCount(analysis?.views || 0);
  }, [analysis, currentUser]);

  if (!isOpen || !analysis) return null;

  const handleLike = async (e) => {
    e.stopPropagation();
    
    if (!currentUser) {
      alert('Please sign in to like analyses');
      return;
    }

    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount(prev => wasLiked ? prev - 1 : prev + 1);

    try {
      if (onLike) {
        await onLike(analysis.id);
      }
    } catch (error) {
      setIsLiked(wasLiked);
      setLikeCount(prev => wasLiked ? prev + 1 : prev - 1);
      console.error('Like failed:', error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const ScoreCircle = ({ score, size = 'lg' }) => {
    const radius = size === 'sm' ? 20 : 32;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    
    return (
      <div className={`relative ${size === 'sm' ? 'w-12 h-12' : 'w-20 h-20'}`}>
        <svg className="transform -rotate-90 w-full h-full">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            className="text-slate-700"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={getScoreColor(score)}
            style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold ${size === 'sm' ? 'text-sm' : 'text-xl'} text-white`}>
            {Math.round(score)}
          </span>
          {size === 'lg' && (
            <span className="text-xs text-slate-400">/ 100</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
          
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">{analysis.score || analysis.overallScore}</span>
              </div>
              <h2 className="text-xl font-bold text-white">DETAILED ANALYSIS</h2>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="p-6">
              
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3">{analysis.url}</h3>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-400">Industry:</span>
                      <span className="font-medium">{analysis.industry || 'Technology'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-400">Analyzed:</span>
                      <span className="font-medium">{analysis.lastAnalyzed || 'Recently'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-300">
                      <Eye className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-400">Views:</span>
                      <span className="font-medium">{viewCount}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-300">
                      <Heart className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-400">Likes:</span>
                      <span className="font-medium">{likeCount}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                      analysis.demo_mode 
                        ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
                        : 'bg-green-500/20 text-green-300 border border-green-500/30'
                    }`}>
                      {analysis.demo_mode ? '🎭' : '🤖'}
                      <span>Source: {analysis.demo_mode ? 'Demo Analysis' : 'AI Analysis'}</span>
                    </span>
                  </div>
                </div>
                
                <div className="text-center ml-6">
                  <div className="mb-2">
                    <ScoreCircle score={analysis.score || analysis.overallScore} />
                  </div>
                  <div className="text-sm text-slate-400 font-medium">OVERALL SCORE</div>
                  <div className={`text-2xl font-bold ${getScoreColor(analysis.score || analysis.overallScore)}`}>
                    {analysis.score || analysis.overallScore}/100
                  </div>
                </div>
              </div>

              {analysis.scores && (
                <div className="mb-6">
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {Object.entries(analysis.scores).map(([key, score]) => (
                      <div key={key} className="text-center bg-slate-800/50 rounded-lg p-3">
                        <ScoreCircle score={score} size="sm" />
                        <div className="text-xs text-slate-400 mt-1 capitalize">
                          {key.replace('_', ' ')}
                        </div>
                        <div className={`text-sm font-bold mt-1 ${getScoreColor(score)}`}>
                          {Math.round(score)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4 mb-6">
                
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <h4 className="text-green-300 font-semibold">WHAT'S WORKING WELL:</h4>
                  </div>
                  <p className="text-green-200 text-sm leading-relaxed">
                    {analysis.insights?.working || 'Exceptional brand consistency and premium user experience design. Perfect visual hierarchy and seamless navigation flow.'}
                  </p>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-yellow-400" />
                    <h4 className="text-yellow-300 font-semibold">IMPROVEMENT OPPORTUNITIES:</h4>
                  </div>
                  <p className="text-yellow-200 text-sm leading-relaxed">
                    {analysis.insights?.improvements || analysis.topIssue || 'International page load speeds could be optimized with better CDN coverage and image compression.'}
                  </p>
                </div>

                {isExpanded && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-blue-400" />
                      <h4 className="text-blue-300 font-semibold">STRATEGIC INSIGHTS:</h4>
                    </div>
                    <p className="text-blue-200 text-sm leading-relaxed">
                      {analysis.insights?.strategic || 'Market leadership position is strong - focus on emerging market expansion opportunities and premium segment growth.'}
                    </p>
                  </div>
                )}
              </div>

              <div className="text-center mb-6">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center gap-2 mx-auto text-slate-400 hover:text-white transition-colors text-sm px-4 py-2 rounded-lg hover:bg-slate-800"
                >
                  {isExpanded ? (
                    <>
                      <span>Show Less</span>
                      <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <span>Show More Insights</span>
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isLiked 
                      ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-red-300'
                  }`}
                  disabled={!currentUser}
                >
                  <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                  <span className="text-sm">{likeCount}</span>
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm">Share</span>
                  </button>
                  
                  <button
                    onClick={() => window.open(`https://${analysis.url}`, '_blank')}
                    className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-300 px-4 py-2 rounded-lg transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">Visit</span>
                  </button>
                </div>

                <button
                  onClick={() => {
                    onClose();
                    window.location.href = `/search?demo=${encodeURIComponent(analysis.url)}`;
                  }}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="text-sm">Re-analyze</span>
                </button>
              </div>

              {currentTier === 'free' && (
                <div className="mt-6 bg-gradient-to-r from-blue-900/40 to-purple-900/40 border border-blue-700/50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <h4 className="text-white font-semibold">Unlock Full Insights</h4>
                  </div>
                  <p className="text-slate-300 text-sm mb-3">
                    Get unlimited analyses, detailed reports, and premium features
                  </p>
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
                  >
                    <Award className="w-4 h-4 inline mr-2" />
                    Upgrade Now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <EnhancedShareModal
        analysis={analysis}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
};

// Main PublicFeed Component - ✅ COMPLETELY FIXED
const PublicFeed = ({ showMoreDetails = false }) => {
  // Get search context
  const { search } = useSearch();
  
  // Mock auth context for demonstration
  const mockAuth = {
    user: { id: 'demo-user-123', email: 'demo@example.com' },
    isAuthenticated: () => true,
    getCurrentTier: () => 'free'
  };
  
  const user = mockAuth.user;
  const isAuthenticated = mockAuth.isAuthenticated;
  const getCurrentTier = mockAuth.getCurrentTier;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ✅ FIXED: State variables properly initialized
  const [analyses, setAnalyses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  let currentTier = 'free';
  try {
    currentTier = getCurrentTier ? getCurrentTier() : 'free';
  } catch (error) {
    console.warn('Error getting tier:', error);
  }

  // ✅ COMPLETELY FIXED: Remove circular dependency
  const fetchAnalysesFromAPI = useCallback(async (currentLength = 0) => {
    try {
      console.log('🌐 Fetching analyses from public endpoint...');

      const limit = 20;
      const skip = currentLength; // ✅ Use parameter instead of state

      // ✅ Use the PUBLIC endpoint (no auth required)
      const publicEndpoint = '/api/analyses/public';
      
      console.log(`📡 Making request to: ${publicEndpoint}?limit=${limit}&offset=${skip}`);

      // ✅ Remove all authentication headers
      const response = await fetch(`${publicEndpoint}?limit=${limit}&offset=${skip}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.warn(`⚠️ Public endpoint returned ${response.status}, trying fallback...`);
        
        // Try alternative endpoint from your logs
        const fallbackResponse = await fetch('/api/websites/public', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log('✅ Fallback endpoint succeeded:', fallbackData.length);
          return Array.isArray(fallbackData) ? fallbackData : [];
        }
        
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Raw API response:', data);

      // Handle different response formats from your backend
      let analysesArray = [];
      
      if (Array.isArray(data)) {
        analysesArray = data;
      } else if (data.analyses && Array.isArray(data.analyses)) {
        analysesArray = data.analyses;
      } else if (data.data && Array.isArray(data.data)) {
        analysesArray = data.data;
      } else {
        console.warn('⚠️ Unexpected API response format:', data);
        analysesArray = [];
      }

      console.log('✅ Processed analyses:', analysesArray.length);

      // Normalize data structure to match your frontend expectations
      const normalizedAnalyses = analysesArray.map((analysis, index) => ({
        id: analysis.id || `analysis_${Date.now()}_${index}`,
        url: analysis.url || analysis.domain || 'unknown.com',
        domain: analysis.domain || analysis.url?.replace(/^https?:\/\//, '') || 'unknown.com',
        score: analysis.score || analysis.overall_score || analysis.overallScore || 0,
        overallScore: analysis.overallScore || analysis.score || analysis.overall_score || 0,
        scores: analysis.scores || {
          design: analysis.ux_design || analysis.design || 0,
          seo: analysis.seo_fundamentals || analysis.seo || 0,
          performance: analysis.speed_optimization || analysis.performance || 0,
          content: analysis.strategic_copy || analysis.content || 0
        },
        insights: analysis.insights || {
          working: typeof analysis.analysis_summary === 'object' 
            ? analysis.analysis_summary.working 
            : analysis.analysis_summary || 'Analysis completed',
          improvements: typeof analysis.analysis_summary === 'object'
            ? analysis.analysis_summary.improvements
            : 'Optimization opportunities available'
        },
        industry: analysis.industry || 'Business',
        timestamp: analysis.created_at || analysis.timestamp || new Date().toISOString(),
        likes: analysis.likes || analysis.likes_count || Math.floor(Math.random() * 100),
        views: analysis.views || analysis.views_count || Math.floor(Math.random() * 1000),
        likedBy: analysis.likedBy || [],
        demo_mode: analysis.demo_mode || false,
        lastAnalyzed: analysis.lastAnalyzed || analysis.created_at?.split('T')[0] || new Date().toISOString().split('T')[0]
      }));

      // Cache successful responses
      if (normalizedAnalyses.length > 0) {
        localStorage.setItem('publicFeed', JSON.stringify(normalizedAnalyses));
        localStorage.setItem('publicFeed_lastFetch', Date.now().toString());
      }

      return normalizedAnalyses;

    } catch (error) {
      console.error('❌ Failed to fetch from API:', error.message);

      // Enhanced fallback strategy
      console.log('📦 Trying fallback strategies...');
      
      try {
        const fallbackData = JSON.parse(localStorage.getItem('publicFeed') || '[]');
        
        if (fallbackData.length > 0) {
          console.log('📦 Loaded from localStorage:', fallbackData.length);
          return fallbackData;
        }
      } catch (cacheError) {
        console.error('❌ LocalStorage fallback failed:', cacheError);
      }

      // Final fallback - sample data
      console.log('🎭 Using sample data as final fallback...');
      const sampleData = [
        {
          id: 'sample_1',
          url: 'apple.com',
          domain: 'apple.com',
          score: 94,
          overallScore: 94,
          scores: { design: 96, seo: 92, performance: 94, content: 95 },
          insights: { 
            working: 'Exceptional design and user experience with industry-leading performance',
            improvements: 'Minor opportunities in international page load optimization'
          },
          industry: 'Technology',
          timestamp: new Date().toISOString(),
          likes: 156,
          views: 2340,
          likedBy: [],
          demo_mode: true,
          lastAnalyzed: new Date().toISOString().split('T')[0]
        },
        {
          id: 'sample_2',
          url: 'github.com',
          domain: 'github.com', 
          score: 89,
          overallScore: 89,
          scores: { design: 92, seo: 86, performance: 88, content: 90 },
          insights: {
            working: 'Excellent developer-focused interface with clean code presentation',
            improvements: 'Mobile experience could be enhanced for better tablet usage'
          },
          industry: 'Technology',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          likes: 234,
          views: 4560,
          likedBy: [],
          demo_mode: true,
          lastAnalyzed: new Date().toISOString().split('T')[0]
        }
      ];

      return sampleData;
    }
  }, []); // ✅ NO DEPENDENCIES - this prevents the infinite loop

  // ✅ FIXED: Simplified loadData function
  const loadData = useCallback(async () => {
    if (loading) return; // ✅ Prevent multiple simultaneous calls
    
    setLoading(true);
    
    try {
      console.log('🔄 Loading public feed data...');
      const analysesData = await fetchAnalysesFromAPI(0); // ✅ Always start with 0 for initial load
      setAnalyses(analysesData);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Failed to load analyses:', error);
      setError('Failed to load analyses');
    } finally {
      setLoading(false);
    }
  }, [fetchAnalysesFromAPI]); // ✅ Only depend on fetchAnalysesFromAPI

  // ✅ FIXED: Only load data once on mount
  useEffect(() => {
    let mounted = true;
    
    const loadInitialData = async () => {
      if (!mounted) return;
      
      console.log('📄 Initial data load...');
      setLoading(true);
      
      try {
        const analysesData = await fetchAnalysesFromAPI(0); // Start with 0 offset
        if (mounted) {
          setAnalyses(analysesData);
          setLastUpdate(Date.now());
        }
      } catch (error) {
        if (mounted) {
          console.error('Failed to load analyses:', error);
          setError('Failed to load analyses');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();
    
    return () => {
      mounted = false; // Cleanup to prevent state updates on unmounted component
    };
  }, []); // ✅ Empty dependency array - only runs once

  // Filter and sort analyses
  const filteredAndSortedAnalyses = useMemo(() => {
    let filtered = [...analyses];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(analysis => 
        analysis.url?.toLowerCase().includes(search) ||
        analysis.domain?.toLowerCase().includes(search) ||
        analysis.industry?.toLowerCase().includes(search)
      );
    }

    // Apply filters
    if (filterBy === 'demo') {
      filtered = filtered.filter(site => site.demo_mode);
    } else if (filterBy === 'real') {
      filtered = filtered.filter(site => !site.demo_mode);
    } else if (filterBy === 'trending') {
      filtered = filtered.filter(site => site.trending || site.popularity === 'trending');
    }

    // Apply sorting
    switch (sortBy) {
      case 'score':
        filtered.sort((a, b) => (b.overallScore || b.score) - (a.overallScore || a.score));
        break;
      case 'popular':
        filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        break;
      case 'recent':
      default:
        filtered.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
        break;
    }

    return filtered;
  }, [analyses, searchTerm, sortBy, filterBy]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    console.log('🔄 Refreshing feed...');
    setRefreshing(true);
    
    try {
      const analysesData = await fetchAnalysesFromAPI(0); // Reset to 0 for refresh
      setAnalyses(analysesData);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Failed to refresh:', error);
      setError('Failed to refresh feed');
    } finally {
      setRefreshing(false);
    }
  }, [fetchAnalysesFromAPI]);
  
  // Handle like functionality
  const handleLike = useCallback(async (analysisId) => {
    if (!user) {
      alert('Please sign in to like analyses');
      return;
    }
    
    console.log('👍 Handling like for analysis:', analysisId, 'by user:', user.id);
    
    // Update local state
    setAnalyses(prev => prev.map(analysis => {
      if (analysis.id === analysisId) {
        const userHasLiked = analysis.likedBy?.includes(user.id) || false;
        return {
          ...analysis,
          likes: userHasLiked 
            ? Math.max(0, (analysis.likes || 0) - 1)
            : (analysis.likes || 0) + 1,
          likedBy: userHasLiked
            ? (analysis.likedBy || []).filter(id => id !== user.id)
            : [...(analysis.likedBy || []), user.id]
        };
      }
      return analysis;
    }));
  }, [user]);

  // Handle view details
  const handleViewDetails = useCallback((analysis) => {
    console.log('👁️ Viewing analysis for:', analysis.url);
    
    // Increment view count
    setAnalyses(prev => prev.map(item => 
      item.id === analysis.id 
        ? { ...item, views: (item.views || 0) + 1 }
        : item
    ));
    
    // Open modal
    setSelectedAnalysis(analysis);
    setShowModal(true);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedAnalysis(null);
  }, []);

  // Score styling helpers
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  // Popularity indicators
  const getPopularityBadge = (analysis) => {
    if (analysis.trending || analysis.popularity === 'trending') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-300 rounded-full text-xs border border-red-500/30">
          <Flame className="w-3 h-3" />
          <span>Trending</span>
        </div>
      );
    }
    
    if (analysis.popularity === 'popular') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-xs border border-yellow-500/30">
          <Star className="w-3 h-3" />
          <span>Popular</span>
        </div>
      );
    }
    
    return null;
  };

  // Score circle component
  const ScoreCircle = ({ score, size = 'sm' }) => {
    const radius = size === 'sm' ? 16 : 24;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    
    return (
      <div className={`relative ${size === 'sm' ? 'w-12 h-12' : 'w-16 h-16'}`}>
        <svg className="transform -rotate-90 w-full h-full">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth="2"
            fill="transparent"
            className="text-gray-700"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth="2"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={getScoreColor(score)}
            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold ${size === 'sm' ? 'text-xs' : 'text-sm'} text-white`}>
            {Math.round(score)}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-700 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-800 rounded w-2/3 mx-auto"></div>
            </div>
          </div>
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-800/50 rounded-lg p-6 h-32 border border-gray-700"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                Website Intelligence Feed
              </span>
            </h1>
          </div>
          
          <p className="text-xl text-slate-300 mb-6 max-w-3xl mx-auto">
            Discover website performance insights powered by AI. Share analyses across all major platforms 
            and engage with our growing community of developers and designers.
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 mb-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-400" />
              <span>Live Feed</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              <span>{filteredAndSortedAnalyses.length} Websites</span>
            </div>
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-purple-400" />
              <span>{filteredAndSortedAnalyses.filter(w => !w.demo_mode).length} AI Analyzed</span>
            </div>
            <div className="flex items-center gap-2">
              <Share2 className="w-4 h-4 text-pink-400" />
              <span>Multi-Platform Sharing</span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-200">{error}</span>
              </div>
              <button 
                onClick={handleRefresh}
                className="text-red-300 hover:text-red-200 underline"
                disabled={refreshing}
              >
                {refreshing ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          </div>
        )}

        {/* Search and Controls */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg p-4">
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search websites, companies, or domains..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              
              {/* Filters */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <select 
                    value={filterBy} 
                    onChange={(e) => setFilterBy(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">All Results</option>
                    <option value="real">AI Analyzed</option>
                    <option value="demo">Demo Results</option>
                    <option value="trending">Trending</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <SortDesc className="w-4 h-4 text-slate-400" />
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="score">Highest Score</option>
                    <option value="popular">Most Liked</option>
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span>Updated {Math.floor((Date.now() - lastUpdate) / 60000)}m ago</span>
                </div>
                
                <button 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Feed */}
        {filteredAndSortedAnalyses.length > 0 ? (
          <div className="space-y-6 mb-8">
            {filteredAndSortedAnalyses.map((analysis) => {
              const userHasLiked = analysis.likedBy?.includes(user?.id) || false;
              
              return (
                <div 
                  key={analysis.id} 
                  className="group bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl overflow-hidden hover:border-slate-600/50 hover:bg-slate-800/70 transition-all duration-300"
                >
                  <div className="p-6">
                    
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-white truncate">
                            {analysis.url}
                          </h3>
                          {getPopularityBadge(analysis)}
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            analysis.demo_mode 
                              ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
                              : 'bg-green-500/20 text-green-300 border border-green-500/30'
                          }`}>
                            {analysis.demo_mode ? '🎭 Demo' : '🤖 AI'}
                          </span>
                        </div>
                        
                        <div className="flex items-center flex-wrap gap-3 text-sm text-slate-400">
                          <span className="bg-slate-700 px-2 py-1 rounded text-xs">
                            {analysis.industry || 'Business'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {analysis.lastAnalyzed || 'Recently'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {analysis.views || 0} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className={`w-3 h-3 ${userHasLiked ? 'fill-red-400 text-red-400' : ''}`} />
                            {analysis.likes || 0} likes
                          </span>
                        </div>
                      </div>
                      
                      {/* Score Display */}
                      <div className="flex flex-col items-center gap-2">
                        <ScoreCircle score={analysis.overallScore || analysis.score} size="lg" />
                        <div className={`text-xs font-semibold ${getScoreColor(analysis.overallScore || analysis.score)}`}>
                          {analysis.overallScore || analysis.score}/100
                        </div>
                      </div>
                    </div>

                    {/* Top Issue */}
                    <div className="mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-slate-300 leading-relaxed">
                          {analysis.topIssue || analysis.insights?.improvements || 'Performance optimization opportunities identified'}
                        </p>
                      </div>
                    </div>

                    {/* Interaction Bar */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-700/50">
                      <div className="flex items-center gap-3">
                        
                        {/* Like Button */}
                        <button 
                          onClick={() => handleLike(analysis.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                            userHasLiked 
                              ? 'bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30' 
                              : 'bg-slate-700/50 text-slate-300 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/30'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${userHasLiked ? 'fill-current' : ''}`} />
                          <span>{analysis.likes || 0}</span>
                        </button>

                        {/* View Details Button */}
                        <button 
                          onClick={() => handleViewDetails(analysis)}
                          className="flex items-center gap-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 px-4 py-2 rounded-lg text-sm transition-all border border-blue-600/30"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View Analysis</span>
                        </button>
                      </div>

                      {/* Share Hint */}
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Share2 className="w-3 h-3" />
                        <span>Click "View Analysis" to share</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-300 mb-3">No Analyses Found</h3>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              {analyses.length === 0 
                ? "No analyses yet. Be the first to analyze a website!"
                : "No analyses match your current filters. Try adjusting your search criteria."
              }
            </p>
            <button
              onClick={() => window.location.href = '/search'}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              <Brain className="w-5 h-5 inline mr-2" />
              Analyze a Website
            </button>
          </div>
        )}
      </div>

      {/* Enhanced Report Modal with Full Share Functionality */}
      <CompactReportModal
        analysis={selectedAnalysis}
        isOpen={showModal}
        onClose={handleCloseModal}
        onLike={handleLike}
        currentUser={user}
        currentTier={currentTier}
      />
    </div>
  );
};

export default PublicFeed;